import type { EventModel, SchedulerPro } from "@bryntum/schedulerpro-thin";
import type { NotifiableError } from "@bugsnag/js";
import { reportError } from "@/utils/logging-utils";
import { handleUnassignTask } from "../gantt-unassign-handler";
import { showBryntumModal, BryntumModalType } from "../../common/bryntum/bryntum-modals";
import {
   mapEventRecordToUnassignTaskData,
   mapNewTaskToSchedulerEvent,
} from "./resource-schedule-utils";
import type { DragCreateAssignmentParams } from "./resource-schedule-types";
import { ResourceType } from "@/react/prop-types";
import type { ReactNode, RefObject } from "react";
import type { GanttTearsheetRef } from "../gantt-tearsheets";
import type { BryntumSchedulerPro } from "@bryntum/schedulerpro-react-thin";

export interface EditAssignmentParams {
   assignmentId: string;
   projectId: string;
   editAssignmentTearsheetRef: RefObject<GanttTearsheetRef> | undefined;
   schedulerRef: RefObject<BryntumSchedulerPro>;
}

export interface UnassignAssignmentParams {
   eventRecord: EventModel;
   schedulerRef: RefObject<BryntumSchedulerPro>;
}

export interface DeleteAssignmentParams {
   eventRecord: EventModel;
   schedulerRef: RefObject<BryntumSchedulerPro>;
   title: string;
   body: ReactNode;
}

/**
 * Opens the edit assignment tearsheet and updates the event record when saved.
 */
export function handleEditAssignment({
   assignmentId,
   projectId,
   editAssignmentTearsheetRef,
   schedulerRef,
}: EditAssignmentParams): void {
   if (!assignmentId || !projectId || !editAssignmentTearsheetRef?.current) return;

   editAssignmentTearsheetRef.current.handleOpenTearsheet({
      assignmentId: String(assignmentId),
      projectId: String(projectId),
      resourceType: ResourceType.LABOR,
      callback: async ({ newTask }: { newTask: any }) => {
         const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
         if (!scheduler?.project) return;

         const project = scheduler.project as any;
         if (project?.suspendAutoSync) await project.suspendAutoSync();

         try {
            const eventStore = scheduler.project.eventStore;
            const eventToUpdate = eventStore.getById(String(assignmentId));
            if (eventToUpdate) {
               const updated = mapNewTaskToSchedulerEvent(newTask);
               eventToUpdate.set(updated);
               scheduler.refreshRows?.();
            }
            const crud = (scheduler as any).crudManager;
            if (crud?.clearChanges) await crud.clearChanges();
         } finally {
            if (project?.resumeAutoSync) await project.resumeAutoSync();
         }
      },
   });
}

/**
 * Unassigns an assignment by converting it to a request.
 * Suspends auto sync during the operation to prevent conflicts.
 */
export async function handleUnassignAssignment({
   eventRecord,
   schedulerRef,
}: UnassignAssignmentParams): Promise<void> {
   const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
   if (!scheduler?.project || !eventRecord) return;

   const project = scheduler.project as any;

   try {
      if (project?.suspendAutoSync) await project.suspendAutoSync();

      const taskData = mapEventRecordToUnassignTaskData(
         eventRecord as unknown as Parameters<typeof mapEventRecordToUnassignTaskData>[0],
      );
      await handleUnassignTask(taskData);

      const eventToRemove = scheduler.project.eventStore.getById(taskData.id);
      if (eventToRemove) scheduler.project.eventStore.remove(eventToRemove);

      if (project?.acceptChanges) await project.acceptChanges();
   } catch (error) {
      console.error("Error unassigning assignment: ", error);
      reportError(error as NotifiableError, "ResourceSchedule - unassign");
   } finally {
      if (project?.resumeAutoSync) await project.resumeAutoSync();
   }
}

/**
 * Shows a delete confirmation modal and removes the event when confirmed.
 */
export function handleDeleteAssignment({
   eventRecord,
   schedulerRef,
   title,
   body,
}: DeleteAssignmentParams): void {
   const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
   if (!scheduler?.project || !eventRecord) return;

   showBryntumModal({
      type: BryntumModalType.DeleteConfirmation,
      modalId: "gantt-confirmation-modal-container",
      title,
      body,
      onSave: () => {
         scheduler.project.eventStore.remove(eventRecord);
      },
   });
}

/**
 * Handles drag-create assignment rendering on the timeline.
 * If a phantom exists (first callback), removes it first.
 * Then adds the real event and links it to the matching resource.
 */
export async function handleDragCreateAssignment({
   dragCreateContext,
   newTask,
   schedulerRef,
}: DragCreateAssignmentParams): Promise<void> {
   const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
   if (!scheduler?.project) return;

   const project = scheduler.project as any;
   if (project?.suspendAutoSync) await project.suspendAutoSync();

   try {
      if (dragCreateContext) {
         dragCreateContext.finalize(false);
         const crud = (scheduler as any).crudManager;
         if (crud?.clearChanges) await crud.clearChanges();
      }

      const resource = project.resourceStore.findRecord("resourceId", newTask.resourceId);
      if (!resource) return;

      const mapped = mapNewTaskToSchedulerEvent(newTask);
      project.eventStore.add({ ...mapped, id: newTask.id, manuallyScheduled: true });
      project.assignmentStore.add({
         id: `assignment-${newTask.id}`,
         eventId: newTask.id,
         resourceId: resource.id,
      });

      scheduler.refreshRows?.();
      if (project?.acceptChanges) await project.acceptChanges();
   } catch (error) {
      reportError(error as NotifiableError, "ResourceSchedule - dragCreate");
   } finally {
      if (project?.resumeAutoSync) await project.resumeAutoSync();
   }
}
